import type { ComponentType } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { referralsApi } from '../../api/endpoints.ts';
import { useSession } from '../../state/session.tsx';
import type { PlanFeature } from '@shared/constants/index.ts';
import { companyHasPlanFeature, usePlanFeature } from '../plan-gate.tsx';
import { DemoLimitedSection } from '../demo-limit-note.tsx';
import { FounderContactLine } from '../founder-contact.tsx';
import { CalendarColorsSettings } from '../schedule/calendar-colors-settings.tsx';
import CalendarFeedsSection from '../schedule/feeds-section.tsx';
import ApiKeysSection from '../api-keys-section.tsx';
import AiAssistantsSection from '../ai-assistants-section.tsx';
import ReferralsSection from '../referrals-section.tsx';
import ApiDocsSection from '../api-docs-section.tsx';
import BillingSection from '../billing-section.tsx';
import LocationsSection from '../locations-section.tsx';
import PaymentSettingsSection from '../payments/payment-settings-section.tsx';
import PaymentMethodsSection from '../payments/payment-methods-section.tsx';
import AiSection from '../ai-section.tsx';
import DeleteAccountSection from '../delete-account-section.tsx';
import { WidgetSection } from '../widget-section.tsx';
import PhotoQuotesCard from './photo-quotes-card.tsx';
import ChecklistLibraryCard from './checklist-library-card.tsx';
import TipsCard from './tips-card.tsx';
import CompanyDetailsCard from './company-details-card.tsx';
import ExchangeRatesCard from './exchange-rates-card.tsx';
import RecurringHorizonCard from './recurring-horizon-card.tsx';
import WorkingHoursCard from './working-hours-card.tsx';
import RemindersCard from './reminders-card.tsx';
import ChecklistCompletionCard from './checklist-completion-card.tsx';
import LateCancelCard from './late-cancel-card.tsx';
import FillSlotsCard from './fill-slots-card.tsx';
import FollowUpsCard from './follow-ups-card.tsx';
import ReengagementCard from './reengagement-card.tsx';
import NotificationsCard from './notifications-card.tsx';
import TomorrowDigestCard from './tomorrow-digest-card.tsx';
import type { SettingsSectionId } from './settings-routes.ts';
import { useJobChatSettingsAccess } from './job-chat-access.ts';

/**
 * Every section of the Settings hub, once: which display group it sits in,
 * who sees it, and the cards it is made of. The hub's tiles and the
 * one-section pages both read this list, so a tile cannot lead to a page
 * that refuses its reader, and a gate is written in one place. The gates
 * are the long page's (pages/settings.tsx), section for section.
 *
 * Display groups: Essentials (what the next job is quoted, booked and paid
 * with), Everyday, then Advanced. Advanced is split again by `subgroup`
 * (business, communication, …, "Developers & data"), so the web-only power
 * features — API keys and the reference, export, import, logs — sit
 * together, contained, instead of each competing with everyday settings.
 */

export type SettingsGroupId = 'essentials' | 'everyday' | 'advanced';

export const SETTINGS_GROUP_IDS: readonly SettingsGroupId[] = ['essentials', 'everyday', 'advanced'];

export type SettingsSubgroupId =
  'business' | 'scheduling' | 'communication' | 'website' | 'money' | 'access' | 'developers' | 'account';

/** The order of Advanced's subgroups on the hub. */
export const SETTINGS_SUBGROUP_IDS: readonly SettingsSubgroupId[] = [
  'business',
  'scheduling',
  'communication',
  'website',
  'money',
  'access',
  'developers',
  'account',
];

type Session = ReturnType<typeof useSession>;

/** What a section's `when` is decided from. */
export interface SettingsSectionContext {
  company: NonNullable<Session['company']>;
  user: Session['user'];
  can: Session['can'];
  has: (feature: PlanFeature) => boolean;
  publicDemo: boolean;
  /** Job chat: role, plan, and this deployment's capability answer together. */
  showJobChat: boolean;
  /** Refer & Earn: switched on for this deployment (or a public demo). */
  showReferrals: boolean;
}

export interface SettingsSectionDef {
  id: SettingsSectionId;
  group: SettingsGroupId;
  subgroup: SettingsSubgroupId;
  /** The permission and plan gates, exactly as the long page applies them. */
  when: (context: SettingsSectionContext) => boolean;
  /**
   * The section's cards. Null for a section with pages of its own outside
   * `/settings/<slug>` (pricing, numbering, texting, calls, job chat,
   * templates, team, team pay, account, security, logs): its tile leads
   * there (settingsLeafPath).
   */
  Component: ComponentType | null;
  /** The section's name; also the leaf page's title. */
  titleKey: string;
  /** Its hidden search words (settings.nav.keywords). */
  keywordsKey: string;
  /** A short line under the hub tile, for the web-only power features. */
  descriptionKey: string | null;
}

function CompanySettings() {
  return (
    <>
      <CompanyDetailsCard />
      <ExchangeRatesCard />
    </>
  );
}

function RecurringHorizonSettings() {
  const { company } = useSession();
  return <RecurringHorizonCard key={company?.id} />;
}

function PaymentsSettings() {
  return <PaymentSettingsSection chargesCompletedVisits={usePlanFeature('invoicing')} />;
}

function NotificationsSettings() {
  return (
    <>
      <TomorrowDigestCard />
      <NotificationsCard />
    </>
  );
}

function BillingSettings() {
  const { t } = useTranslation();
  const { publicDemo } = useSession();
  return publicDemo ? (
    <DemoLimitedSection heading={t('billing.heading')} note={t('publicDemo.billingNote')} />
  ) : (
    <BillingSection />
  );
}

function ContactSettings() {
  const { t } = useTranslation();
  return (
    <section className="card mb-6" aria-labelledby="contact-heading">
      <h2 id="contact-heading" className="mb-3 text-lg font-semibold">
        {t('settings.contactHeading')}
      </h2>
      <FounderContactLine />
    </section>
  );
}

function CalendarSettings() {
  const { t } = useTranslation();
  const { publicDemo } = useSession();
  return (
    <>
      {publicDemo ? (
        <DemoLimitedSection heading={t('feeds.heading')} note={t('publicDemo.feedsNote')} />
      ) : (
        <CalendarFeedsSection />
      )}
      <CalendarColorsSettings />
    </>
  );
}

function RemindersSettings() {
  return (
    <>
      <RemindersCard />
      <ChecklistCompletionCard />
    </>
  );
}

function ReferralsSettings() {
  const { t } = useTranslation();
  const { publicDemo } = useSession();
  return publicDemo ? (
    <DemoLimitedSection heading={t('referrals.title')} note={t('publicDemo.referralsNote')} />
  ) : (
    <ReferralsSection />
  );
}

function DataSettings() {
  const { t } = useTranslation();
  return (
    <section className="card mb-6" aria-labelledby="export-heading">
      <h2 id="export-heading" className="mb-3 text-lg font-semibold">
        {t('settings.exportHeading')}
      </h2>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('settings.exportDescription')}</p>
      <a className="btn btn-secondary" href="/api/v1/company/export">
        {t('settings.downloadExport')}
      </a>
    </section>
  );
}

/*
  A section whose whole subject the PLAN does not carry is hidden the same
  way a permission hides it — a company on Free has no visit reminders to
  configure and no invoice tips to set. Billing is never hidden: it is where
  the plan can be changed, and hiding the way up would be the one gate with
  no way through. The API and texting cards stay visible on purpose; each
  renders its own upgrade note, which is the older pattern and the right one
  for a card that has something to say to a company that cannot use it yet.

  Listed in display order: Essentials, Everyday, then Advanced.
*/
const SECTIONS: ReadonlyArray<
  Omit<SettingsSectionDef, 'titleKey' | 'keywordsKey' | 'descriptionKey'> & {
    /** Name and search words under another id: the library tile opens all three template managers. */
    copy?: string;
    /** Has a one-line description under its hub tile (settings.hub.descriptions). */
    described?: true;
  }
> = [
  // Essentials
  { id: 'company', group: 'essentials', subgroup: 'business', when: () => true, Component: CompanySettings },
  // Pricing & quotes and numbering are pages of their own under
  // /settings/pricing (SETTINGS_SECTION_PAGES), in both shapes of Settings.
  { id: 'pricing', group: 'essentials', subgroup: 'business', when: () => true, Component: null },
  {
    id: 'library',
    group: 'essentials',
    subgroup: 'business',
    when: ({ can }) => can('templates:read'),
    Component: null,
    copy: 'templates',
  },
  // The checklist library is a page of its own; this is the way in. Same
  // gates as the page and its list endpoint, so the link never opens an error.
  {
    id: 'checklists',
    group: 'essentials',
    subgroup: 'business',
    when: ({ can, has }) => has('scheduling') && can('customers:read'),
    Component: ChecklistLibraryCard,
  },
  {
    id: 'hours',
    group: 'essentials',
    subgroup: 'scheduling',
    when: ({ has }) => has('scheduling'),
    Component: WorkingHoursCard,
  },
  {
    id: 'recurringHorizon',
    group: 'essentials',
    subgroup: 'scheduling',
    when: ({ can, has }) => has('scheduling') && can('company:write'),
    Component: RecurringHorizonSettings,
  },
  // Every plan: Free takes card payment links for one job at a time
  // (`single_job`), so its Stripe account is set up here too.
  {
    id: 'payments',
    group: 'essentials',
    subgroup: 'money',
    when: ({ can, has }) => can('payments:read') && has('single_job'),
    Component: PaymentsSettings,
    described: true,
  },
  // Every plan: the types a payment on a quote is recorded as (Cash, Check,
  // Zelle…) are Free's too.
  {
    id: 'paymentMethods',
    group: 'essentials',
    subgroup: 'money',
    when: ({ can, has }) => can('payments:read') && has('quote_payments'),
    Component: PaymentMethodsSection,
    described: true,
  },

  // Everyday
  {
    id: 'notifications',
    group: 'everyday',
    subgroup: 'communication',
    when: () => true,
    Component: NotificationsSettings,
  },
  {
    id: 'billing',
    group: 'everyday',
    subgroup: 'money',
    when: () => true,
    Component: BillingSettings,
    described: true,
  },
  {
    id: 'team',
    group: 'everyday',
    subgroup: 'access',
    when: ({ can }) => can('users:manage'),
    Component: null,
  },
  { id: 'account', group: 'everyday', subgroup: 'account', when: () => true, Component: null },
  // Beside the account's other settings rather than under "access": these are
  // the signed-in person's own protections, not the company's roster.
  { id: 'security', group: 'everyday', subgroup: 'account', when: () => true, Component: null },
  { id: 'contact', group: 'everyday', subgroup: 'account', when: () => true, Component: ContactSettings },

  // Advanced
  { id: 'numbering', group: 'advanced', subgroup: 'business', when: () => true, Component: null },
  {
    id: 'locations',
    group: 'advanced',
    subgroup: 'business',
    when: ({ can }) => can('company:read'),
    Component: LocationsSection,
    described: true,
  },
  {
    id: 'calendar',
    group: 'advanced',
    subgroup: 'scheduling',
    when: ({ can, has }) => can('schedule:read') && has('scheduling'),
    Component: CalendarSettings,
    described: true,
  },
  {
    id: 'reminders',
    group: 'advanced',
    subgroup: 'communication',
    when: ({ has }) => has('scheduling'),
    Component: RemindersSettings,
  },
  {
    id: 'lateCancel',
    group: 'advanced',
    subgroup: 'communication',
    when: ({ has }) => has('scheduling'),
    Component: LateCancelCard,
  },
  {
    id: 'fillSlots',
    group: 'advanced',
    subgroup: 'communication',
    when: ({ can }) => can('company:read'),
    Component: FillSlotsCard,
  },
  { id: 'followups', group: 'advanced', subgroup: 'communication', when: () => true, Component: FollowUpsCard },
  {
    id: 'reengagement',
    group: 'advanced',
    subgroup: 'communication',
    when: ({ user, can }) => user?.role === 'owner' && can('company:write'),
    Component: ReengagementCard,
  },
  // Texting setup, calls and job chat are pages of their own under
  // /settings/messaging (SETTINGS_SECTION_PAGES), in both shapes of
  // Settings. Calls only where this installation answers calls: elsewhere
  // its cards render nothing.
  {
    id: 'texting',
    group: 'advanced',
    subgroup: 'communication',
    when: ({ can }) => can('company:write'),
    Component: null,
    described: true,
  },
  {
    id: 'calls',
    group: 'advanced',
    subgroup: 'communication',
    when: ({ company, can }) => can('company:write') && company.voiceInboxAvailable === true,
    Component: null,
    described: true,
  },
  {
    id: 'jobChat',
    group: 'advanced',
    subgroup: 'communication',
    when: ({ showJobChat }) => showJobChat,
    Component: null,
  },
  {
    id: 'ai',
    group: 'advanced',
    subgroup: 'communication',
    when: ({ can }) => can('company:read'),
    Component: AiSection,
    described: true,
  },
  {
    id: 'widget',
    group: 'advanced',
    subgroup: 'website',
    when: ({ can, has }) => can('company:write') && has('online_intake'),
    Component: WidgetSection,
    described: true,
  },
  // The photo-quote upload page rides the same request link, so it sits
  // beside the form's own settings. Behind the deployment flag as well.
  {
    id: 'photoQuotes',
    group: 'advanced',
    subgroup: 'website',
    when: ({ company, can, has }) => company.photoQuotesEnabled && can('company:read') && has('online_intake'),
    Component: PhotoQuotesCard,
    described: true,
  },
  {
    id: 'tips',
    group: 'advanced',
    subgroup: 'money',
    when: ({ has }) => has('invoicing'),
    Component: TipsCard,
    described: true,
  },
  // Pay and job profit (0035): the owner edits, managers read, crew never.
  {
    id: 'teamPay',
    group: 'advanced',
    subgroup: 'access',
    when: ({ can, has }) => can('reports:read') && has('job_profit'),
    Component: null,
    described: true,
  },
  // ChatGPT & Claude: everybody connects their own, and it works as them —
  // limited to what their role allows — so everybody gets the section.
  {
    id: 'aiAssistants',
    group: 'advanced',
    subgroup: 'access',
    when: () => true,
    Component: AiAssistantsSection,
    described: true,
  },
  {
    id: 'apiKeys',
    group: 'advanced',
    subgroup: 'developers',
    when: ({ can }) => can('api_keys:manage'),
    Component: ApiKeysSection,
    described: true,
  },
  {
    id: 'apiDocs',
    group: 'advanced',
    subgroup: 'developers',
    when: ({ can }) => can('api_keys:manage'),
    Component: ApiDocsSection,
    described: true,
  },
  {
    id: 'referrals',
    group: 'advanced',
    subgroup: 'developers',
    when: ({ showReferrals }) => showReferrals,
    Component: ReferralsSettings,
    described: true,
  },
  {
    id: 'data',
    group: 'advanced',
    subgroup: 'developers',
    when: ({ can }) => can('company:export'),
    Component: DataSettings,
    described: true,
  },
  // A page of its own that Settings links to beside export: the importer
  // (also linked from Customers). Same gate as the page, so the tile never
  // opens a refusal.
  {
    id: 'customerImport',
    group: 'advanced',
    subgroup: 'developers',
    when: ({ can }) => can('customers:write'),
    Component: null,
    described: true,
  },
  // With the Work hub on, Logs left the sidebar for it; this is its second
  // way in, as on the long page.
  {
    id: 'logs',
    group: 'advanced',
    subgroup: 'developers',
    when: ({ company, can }) => company.navSecondaryHub === true && can('logs:read'),
    Component: null,
    described: true,
  },
  { id: 'delete', group: 'advanced', subgroup: 'account', when: () => true, Component: DeleteAccountSection },
];

export const SETTINGS_SECTION_REGISTRY: readonly SettingsSectionDef[] = SECTIONS.map(({ copy, described, ...def }) => ({
  ...def,
  titleKey: `settings.nav.sections.${copy ?? def.id}`,
  keywordsKey: `settings.nav.keywords.${copy ?? def.id}`,
  descriptionKey: described ? `settings.hub.descriptions.${def.id}` : null,
}));

export function settingsSectionDef(id: string): SettingsSectionDef | undefined {
  return SETTINGS_SECTION_REGISTRY.find((def) => def.id === id);
}

/**
 * What every section's `when` is decided from, and whether all of it is
 * known yet.
 *
 * Job chat's entry asks the same question as its page and its card
 * (useJobChatSettingsAccess).
 *
 * Refer & Earn renders nothing while the program is switched off on this
 * deployment. Same key as the section's own read, so this costs no request.
 */
export function useSettingsSectionContext(): { context: SettingsSectionContext | null; ready: boolean } {
  const { company, user, can, publicDemo } = useSession();
  const jobChat = useJobChatSettingsAccess();
  const referrals = useQuery({
    queryKey: ['referrals'],
    queryFn: () => referralsApi.overview(),
    enabled: company !== null && !publicDemo,
  });

  if (!company) return { context: null, ready: false };
  const has = (feature: PlanFeature) => companyHasPlanFeature(company, feature);
  return {
    context: {
      company,
      user,
      can,
      has,
      publicDemo: Boolean(publicDemo),
      showJobChat: jobChat.available,
      showReferrals: Boolean(publicDemo) || referrals.data?.enabled === true,
    },
    // Job chat and Refer & Earn only exist once their queries answer.
    ready: !jobChat.loading && !referrals.isLoading,
  };
}

/** The sections this person sees, in display order. */
export function visibleSettingsSections(context: SettingsSectionContext): SettingsSectionDef[] {
  return SETTINGS_SECTION_REGISTRY.filter((def) => def.when(context));
}
